package com.siriusxm.cmc.views

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

private const val STORAGE_KEY = "cmc-starred-apps"

/**
 * State manager for starred applications using browser localStorage.
 * Starred apps are persisted across sessions and displayed at the top
 * of the applications list.
 */
class StarredAppsState {
    /**
     * Set of application paths that are currently starred.
     */
    val starredPaths = mutableStateOf<Set<String>>(emptySet())

    /**
     * Whether the state has been initialized from localStorage.
     */
    val isInitialized = mutableStateOf(false)

    /**
     * Loads starred apps from localStorage.
     */
    fun load() {
        try {
            val stored = window.localStorage.getItem(STORAGE_KEY)
            if (stored != null) {
                val paths = Json.decodeFromString<List<String>>(stored)
                starredPaths.value = paths.toSet()
            }
        } catch (e: Exception) {
            console.error("Failed to load starred apps from localStorage", e)
        }
        isInitialized.value = true
    }

    /**
     * Saves starred apps to localStorage.
     */
    private fun save() {
        try {
            val json = Json.encodeToString(starredPaths.value.toList())
            window.localStorage.setItem(STORAGE_KEY, json)
        } catch (e: Exception) {
            console.error("Failed to save starred apps to localStorage", e)
        }
    }

    /**
     * Checks if an application is starred.
     *
     * @param path The application path
     * @return true if the application is starred
     */
    fun isStarred(path: String): Boolean {
        return path in starredPaths.value
    }

    /**
     * Toggles the starred state of an application.
     *
     * @param path The application path to toggle
     */
    fun toggle(path: String) {
        starredPaths.value = if (isStarred(path)) {
            starredPaths.value - path
        } else {
            starredPaths.value + path
        }
        save()
    }

    companion object {
        val instance = StarredAppsState()
    }
}

/**
 * Composable function to access the starred apps state.
 * Automatically loads from localStorage on first access.
 *
 * @return The StarredAppsState instance
 */
@Composable
fun rememberStarredApps(): StarredAppsState {
    val state = remember { StarredAppsState.instance }

    LaunchedEffect(Unit) {
        if (!state.isInitialized.value) {
            state.load()
        }
    }

    return state
}
